/**
 * QMP-FE-1 (F2) — STEP 1: Quality Work Packages & Parties.
 *
 * The table of mockup v79 (`#w1`, `renderRows`): one row per live package,
 * Executing / Verification / Acceptance selects, remove, "+ Add a package…".
 * Copy verbatim from the mockup; the ambar notes of v43 are gone (D14.1, v46).
 *
 * LIVE, ROW BY ROW. Each change is saved on its own the moment it is made, and
 * the plan is re-read afterwards (`qmp.reload`) — the row then shows what the
 * server holds, never what the screen hoped:
 *   · Verification / Acceptance -> PUT /qwps/{id}/parties with BOTH positions;
 *   · Executing, while "To be appointed" -> POST .../executing-party/appoint;
 *   · Executing, already named -> the existing transfer (PATCH /qwps/{id}).
 *     A package with records answers with its own reason requirement, and that
 *     answer is shown on the row; the reason flow is not rebuilt here.
 * A lost race (409 QWP_STRUCTURE_CHANGED) reloads and says so on the row.
 * Every other refusal is printed on the row it belongs to.
 *
 * GOVERNANCE IS THE SERVER'S. `can_govern` from the package list only decides
 * whether the editors are drawn; the 403 is what decides.
 */
import React from 'react';
import {
  getProjectCompanies, listQwps, createQwp, updateQwp, deleteQwp,
  updateQwpParties, appointQwpExecutingParty,
} from '../../lib/api';
import { apiErrorMessage } from '../../pages/TestRegisterPage';

export const TBA = '__tba__';
export const NONE = '__none__';
const TBA_LABEL = 'To be appointed';
const NONE_LABEL = 'None';
export const STRUCTURE_CHANGED_NOTICE = 'Someone changed this package — reloaded';

// Mockup `#tipE` / `#tipV` / `#tipA`, verbatim.
export const TIPS = {
  E: {
    title: 'Executing Party — does the work',
    body: (
      <>
        The company responsible for carrying out the work and providing the QA records and
        sign-offs required for its scope. The Executing Party is responsible for delivering its
        work in accordance with the project requirements.
      </>
    ),
  },
  V: {
    title: 'Verification Party — checks the work',
    body: (
      <>
        The company responsible for checking the work and QA evidence provided by the Executing
        Party. If this role is not part of the package&apos;s ConstructQA quality process,
        select <b>None</b>.
      </>
    ),
  },
  A: {
    title: 'Acceptance Party — formally accepts the work',
    body: (
      <>
        The company responsible for formally accepting the completed work. If this role is not
        part of the package&apos;s ConstructQA quality process, select <b>None</b>.
      </>
    ),
  },
};

const isStructureChanged = (err) => {
  const detail = err?.response?.data?.detail;
  return err?.response?.status === 409 && detail && typeof detail === 'object'
    && (detail.error === 'QWP_STRUCTURE_CHANGED' || detail.code === 'QWP_STRUCTURE_CHANGED');
};

// Mockup `select.control` / `input.control`.
const CONTROL = 'w-full bg-[#0b1322] border border-[#1c2941] text-[#f4f7fc] rounded-[8px] '
  + 'px-[10px] py-[8px] text-[12.5px] placeholder:text-[#3d4c68] disabled:opacity-60';
// Mockup `.struct td` (first/last cells add the side border and the radius).
const TD = 'bg-[#101a2e] px-[10px] py-[10px] align-middle border-y border-[#16223a]';
const TD_FIRST = `${TD} border-l rounded-l-[9px]`;
const TD_LAST = `${TD} border-r rounded-r-[9px]`;
const TH = 'text-left text-[10.5px] tracking-[.08em] uppercase text-[#5b6b85] font-bold px-[10px] pb-[2px]';
const SMALL_BTN = 'px-[12px] py-[6px] rounded-[8px] text-[12px] font-bold';

function QButton({ tipKey, onOpen }) {
  return (
    <button
      type="button"
      data-testid={`qs-tip-btn-${tipKey}`}
      onClick={(e) => onOpen(tipKey, e.currentTarget)}
      className="ml-[5px] w-[17px] h-[17px] rounded-full border border-[#1c2941] text-[#5b6b85] text-[10px] leading-none inline-flex items-center justify-center align-middle hover:text-[#f4f7fc] hover:border-[#2a3a5c]"
    >
      ?
    </button>
  );
}

function PartySelect({ value, options, onChange, disabled, testId }) {
  return (
    <select
      className={CONTROL}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export default function PackagesPartiesStep({ projectId, qmp }) {
  const { structure, error: loadError, reload } = qmp;
  const [companies, setCompanies] = React.useState([]);
  const [canGovern, setCanGovern] = React.useState(true);
  const [pending, setPending] = React.useState({});
  const [busy, setBusy] = React.useState({});
  const [rowError, setRowError] = React.useState({});
  const [rowNotice, setRowNotice] = React.useState({});
  const [names, setNames] = React.useState({});
  const [confirmRemove, setConfirmRemove] = React.useState(null);
  const [draft, setDraft] = React.useState(null);
  const [tip, setTip] = React.useState(null);
  const popRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    Promise.resolve(getProjectCompanies(projectId))
      .then((rows) => { if (!cancelled) setCompanies(Array.isArray(rows) ? rows : []); })
      .catch(() => {});
    Promise.resolve(listQwps(projectId))
      .then((r) => {
        const rows = Array.isArray(r?.data) ? r.data : [];
        if (!cancelled && rows.length) setCanGovern(rows.some((q) => q.can_govern));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [projectId]);

  React.useEffect(() => {
    if (!tip) return undefined;
    const close = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-testid^="qs-tip-btn-"]')) return;
      setTip(null);
    };
    const onScroll = () => setTip(null);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll);
    };
  }, [tip]);

  // Mockup `openPop`: below the button, kept inside the viewport; a second
  // click on the same "?" closes it.
  const openTip = (key, btn) => {
    if (tip && tip.key === key) { setTip(null); return; }
    const r = btn.getBoundingClientRect();
    const W = window.innerWidth || 1440;
    const w = Math.min(380, W - 24);
    setTip({ key, left: Math.max(12, Math.min(r.left, W - w - 12)), top: r.bottom + 8 });
  };

  const companyOptions = (extra) => {
    const opts = companies.map((c) => ({ value: c.id, label: c.name || c.short_name || '' }));
    (extra || []).forEach((p) => {
      if (p && p.participation_id && !opts.some((o) => o.value === p.participation_id)) {
        opts.push({ value: p.participation_id, label: p.name || '' });
      }
    });
    return opts;
  };

  const run = async (qwpId, patch, call) => {
    setPending((p) => ({ ...p, [qwpId]: { ...(p[qwpId] || {}), ...patch } }));
    setBusy((b) => ({ ...b, [qwpId]: true }));
    setRowError((e) => ({ ...e, [qwpId]: '' }));
    setRowNotice((n) => ({ ...n, [qwpId]: '' }));
    try {
      await call();
    } catch (err) {
      if (isStructureChanged(err)) {
        setRowNotice((n) => ({ ...n, [qwpId]: STRUCTURE_CHANGED_NOTICE }));
      } else {
        setRowError((e) => ({ ...e, [qwpId]: apiErrorMessage(err, 'Could not save this package') }));
      }
    }
    await reload();
    setPending((p) => { const next = { ...p }; delete next[qwpId]; return next; });
    setBusy((b) => ({ ...b, [qwpId]: false }));
  };

  const partyId = (party) => (party && party.participation_id) || null;

  const onExecuting = (row, value) => {
    if (value === TBA) return;
    if (!row.executing.appointed) {
      run(row.qwp_id, { ep: value }, () => appointQwpExecutingParty(row.qwp_id, value));
    } else if (value !== row.executing.participation_id) {
      run(row.qwp_id, { ep: value }, () => updateQwp(row.qwp_id, { owner_company_id: value }));
    }
  };

  const onPosition = (row, key, value) => {
    const next = value === NONE ? null : value;
    const body = {
      verification_company_id: partyId(row.verification),
      acceptance_company_id: partyId(row.acceptance),
    };
    body[key === 'vp' ? 'verification_company_id' : 'acceptance_company_id'] = next;
    run(row.qwp_id, { [key]: value }, () => updateQwpParties(row.qwp_id, body));
  };

  const onRename = (row) => {
    const typed = names[row.qwp_id];
    setNames((n) => { const next = { ...n }; delete next[row.qwp_id]; return next; });
    if (typed === undefined) return;
    const name = typed.trim();
    if (!name || name === row.name) return;
    run(row.qwp_id, { name }, () => updateQwp(row.qwp_id, { name }));
  };

  const onRemove = (row) => {
    setConfirmRemove(null);
    run(row.qwp_id, {}, () => deleteQwp(row.qwp_id));
  };

  const addPackage = async () => {
    const name = (draft.name || '').trim();
    if (!name) return;
    setDraft((d) => ({ ...d, busy: true, error: '' }));
    let created;
    try {
      const body = draft.ep === TBA
        ? { name, executing_party_appointed: false }
        : { name, owner_company_id: draft.ep };
      const res = await createQwp(projectId, body);
      created = res?.data;
    } catch (err) {
      setDraft((d) => ({ ...d, busy: false, error: apiErrorMessage(err, 'Could not add this package') }));
      return;
    }
    if (created?.id && (draft.vp !== NONE || draft.ap !== NONE)) {
      try {
        await updateQwpParties(created.id, {
          verification_company_id: draft.vp === NONE ? null : draft.vp,
          acceptance_company_id: draft.ap === NONE ? null : draft.ap,
        });
      } catch (err) {
        setRowError((e) => ({
          ...e, [created.id]: apiErrorMessage(err, 'Could not save this package'),
        }));
      }
    }
    setDraft(null);
    await reload();
  };

  const rows = structure || [];
  const tipData = tip ? TIPS[tip.key] : null;

  return (
    <div data-testid="qs-step1">
      <div className="text-[17px] font-bold flex items-center gap-[10px] mb-[8px] flex-wrap">
        Quality Work Packages &amp; Parties
        <span
          className="inline-flex text-[10px] font-bold tracking-[.08em] px-[9px] py-[3px] rounded-[6px] text-[#f59e0b] border border-[rgba(245,158,11,.4)] bg-[rgba(245,158,11,.1)]"
          data-testid="qs-active-badge"
        >
          Active when saved
        </span>
      </div>
      <div className="text-[#94a3b8] text-[13px] mb-[16px] max-w-[760px]">
        Set the Executing Party for each package. Add Verification and Acceptance only where the
        project needs them.
      </div>
      <details className="group -mt-[8px] mb-[16px] max-w-[760px]" data-testid="qs-how1">
        <summary className="cursor-pointer list-none inline-flex items-center gap-[6px] text-[12px] text-[#93c5fd] [&::-webkit-details-marker]:hidden">
          <span className="inline-flex w-[16px] h-[16px] rounded-full border border-[#3b82f6] items-center justify-center text-[10px]">?</span>
          How this works
          <span className="hidden group-open:inline text-[#94a3b8]">{' — hide'}</span>
        </summary>
        <div className="text-[#94a3b8] text-[12.5px] mt-[8px] leading-[1.5] [&_b]:text-[#f4f7fc]">
          The Executing Party is required for every package. Add Verification and Acceptance
          Parties only where those roles are part of the project&apos;s quality process. If the
          Executing Party has not been appointed yet, select <b>To be appointed</b> and name the
          company later. These positions are then used by the ITP, and when an ITC is created,
          ConstructQA records the company responsible for each required sign-off. If Verification
          and Acceptance are not required, the package is managed as internal QA by the Executing
          Party.
        </div>
      </details>

      {loadError && !rows.length && (
        <div className="mt-[10px] mb-[10px] text-[12.5px] text-[#fca5a5]" data-testid="qs-load-error">
          {loadError}
        </div>
      )}

      <table className="w-full border-separate border-spacing-x-0 border-spacing-y-[8px]" data-testid="qs-struct">
        <thead>
          <tr>
            <th className={TH} style={{ width: '26%' }}>Quality Work Package</th>
            <th className={TH} style={{ width: '24%' }}>
              Executing Party <QButton tipKey="E" onOpen={openTip} />
            </th>
            <th className={TH} style={{ width: '24%' }}>
              Verification Party <QButton tipKey="V" onOpen={openTip} />
            </th>
            <th className={TH} style={{ width: '22%' }}>
              Acceptance Party <QButton tipKey="A" onOpen={openTip} />
            </th>
            <th className={TH} aria-label="Remove" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const id = row.qwp_id;
            const p = pending[id] || {};
            const locked = !canGovern || Boolean(busy[id]);
            const appointed = row.executing?.appointed !== false;
            const epValue = p.ep ?? (appointed ? (row.executing?.participation_id || '') : TBA);
            const vpValue = p.vp ?? (partyId(row.verification) || NONE);
            const apValue = p.ap ?? (partyId(row.acceptance) || NONE);
            const known = [row.executing, row.verification, row.acceptance];
            const epOptions = appointed
              ? companyOptions(known)
              : [{ value: TBA, label: TBA_LABEL }, ...companyOptions(known)];
            const posOptions = [...companyOptions(known), { value: NONE, label: NONE_LABEL }];
            const strip = rowError[id] || rowNotice[id] || confirmRemove === id;
            return (
              <React.Fragment key={id}>
                <tr data-testid={`qs-row-${id}`}>
                  <td className={TD_FIRST}>
                    <input
                      className={CONTROL}
                      value={names[id] ?? p.name ?? row.name}
                      placeholder="Package title"
                      disabled={locked}
                      onChange={(e) => setNames((n) => ({ ...n, [id]: e.target.value }))}
                      onBlur={() => onRename(row)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                      data-testid={`qs-name-${id}`}
                    />
                  </td>
                  <td className={TD}>
                    <PartySelect
                      value={epValue}
                      options={epOptions}
                      disabled={locked}
                      onChange={(v) => onExecuting(row, v)}
                      testId={`qs-ep-${id}`}
                    />
                  </td>
                  <td className={TD}>
                    <PartySelect
                      value={vpValue}
                      options={posOptions}
                      disabled={locked}
                      onChange={(v) => onPosition(row, 'vp', v)}
                      testId={`qs-vp-${id}`}
                    />
                  </td>
                  <td className={TD}>
                    <PartySelect
                      value={apValue}
                      options={posOptions}
                      disabled={locked}
                      onChange={(v) => onPosition(row, 'ap', v)}
                      testId={`qs-ap-${id}`}
                    />
                  </td>
                  <td className={TD_LAST}>
                    {canGovern && (
                      <button
                        type="button"
                        className="text-[#5b6b85] text-[15px] px-[4px] hover:text-[#f4f7fc] disabled:opacity-60"
                        disabled={locked}
                        onClick={() => setConfirmRemove(id)}
                        aria-label={`Remove ${row.name}`}
                        data-testid={`qs-remove-${id}`}
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
                {strip && (
                  <tr data-testid={`qs-strip-${id}`}>
                    <td colSpan={5} className="px-[10px] pb-[2px] text-[12.5px]">
                      {rowError[id] && (
                        <div className="text-[#fca5a5]" data-testid={`qs-row-error-${id}`}>
                          {rowError[id]}
                        </div>
                      )}
                      {rowNotice[id] && (
                        <div className="text-[#fbbf24]" data-testid={`qs-row-notice-${id}`}>
                          {rowNotice[id]}
                        </div>
                      )}
                      {confirmRemove === id && (
                        <div className="flex items-center gap-[10px] text-[#94a3b8]" data-testid={`qs-remove-confirm-${id}`}>
                          <span>
                            Remove <b className="text-[#f4f7fc]">{row.name}</b>?
                          </span>
                          <button
                            type="button"
                            className={`${SMALL_BTN} bg-[#f97316] text-[#180b02]`}
                            onClick={() => onRemove(row)}
                            data-testid={`qs-remove-yes-${id}`}
                          >
                            Remove
                          </button>
                          <button
                            type="button"
                            className={`${SMALL_BTN} border border-[#1c2941] text-[#94a3b8]`}
                            onClick={() => setConfirmRemove(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {draft && (
            <>
              <tr data-testid="qs-draft-row">
                <td className={TD_FIRST}>
                  <input
                    className={CONTROL}
                    value={draft.name}
                    placeholder="Package title"
                    disabled={draft.busy}
                    onChange={(e) => { const v = e.target.value; setDraft((d) => ({ ...d, name: v })); }}
                    data-testid="qs-draft-name"
                  />
                </td>
                <td className={TD}>
                  <PartySelect
                    value={draft.ep}
                    options={[{ value: TBA, label: TBA_LABEL }, ...companyOptions()]}
                    disabled={draft.busy}
                    onChange={(v) => setDraft((d) => ({ ...d, ep: v }))}
                    testId="qs-draft-ep"
                  />
                </td>
                <td className={TD}>
                  <PartySelect
                    value={draft.vp}
                    options={[...companyOptions(), { value: NONE, label: NONE_LABEL }]}
                    disabled={draft.busy}
                    onChange={(v) => setDraft((d) => ({ ...d, vp: v }))}
                    testId="qs-draft-vp"
                  />
                </td>
                <td className={TD}>
                  <PartySelect
                    value={draft.ap}
                    options={[...companyOptions(), { value: NONE, label: NONE_LABEL }]}
                    disabled={draft.busy}
                    onChange={(v) => setDraft((d) => ({ ...d, ap: v }))}
                    testId="qs-draft-ap"
                  />
                </td>
                <td className={TD_LAST}>
                  <button
                    type="button"
                    className="text-[#5b6b85] text-[15px] px-[4px] hover:text-[#f4f7fc]"
                    onClick={() => setDraft(null)}
                    aria-label="Cancel"
                  >
                    ✕
                  </button>
                </td>
              </tr>
              <tr>
                <td colSpan={5} className="px-[10px] pb-[2px] text-[12.5px]">
                  <div className="flex items-center gap-[10px]">
                    <button
                      type="button"
                      className={`${SMALL_BTN} bg-[#f97316] text-[#180b02] disabled:opacity-45 disabled:cursor-not-allowed`}
                      disabled={draft.busy || !(draft.name || '').trim()}
                      onClick={addPackage}
                      data-testid="qs-draft-add"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      className={`${SMALL_BTN} border border-[#1c2941] text-[#94a3b8]`}
                      disabled={draft.busy}
                      onClick={() => setDraft(null)}
                    >
                      Cancel
                    </button>
                    {draft.error && (
                      <span className="text-[#fca5a5]" data-testid="qs-draft-error">{draft.error}</span>
                    )}
                  </div>
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>

      {canGovern && !draft && (
        <button
          type="button"
          className="w-full text-left mt-[2px] border border-dashed border-[#1c2941] text-[#94a3b8] rounded-[9px] px-[14px] py-[10px] text-[12.5px] hover:text-[#f4f7fc] hover:border-[#2a3a5c]"
          onClick={() => setDraft({ name: '', ep: TBA, vp: NONE, ap: NONE, busy: false, error: '' })}
          data-testid="qs-add"
        >
          + Add a package…
        </button>
      )}

      {tipData && (
        <div
          ref={popRef}
          role="tooltip"
          className="fixed z-[90] w-[380px] max-w-[calc(100vw-24px)] bg-[#101a2e] border border-[rgba(249,115,22,.55)] rounded-[10px] px-[16px] py-[14px] text-[12.5px] leading-[1.55] text-[#94a3b8] shadow-[0_12px_34px_rgba(0,0,0,.45)] [&_b]:text-[#f4f7fc]"
          style={{ left: tip.left, top: tip.top }}
          data-testid="qs-tip"
        >
          <span className="block text-[#f4f7fc] font-bold mb-[6px]">{tipData.title}</span>
          {tipData.body}
        </div>
      )}
    </div>
  );
}
